import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Container,
    Fade,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';

function Admin() {
    const [pin, setPin] = useState('');
    const [open, setOpen] = useState(true);
    const [error, setError] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reportType, setReportType] = useState('');
    const [reportData, setReportData] = useState(null);
    const navigate = useNavigate();
    // Add these state variables with other useState declarations
    const [vehicleNumber, setVehicleNumber] = useState('');
    const [vehicleStartDate, setVehicleStartDate] = useState('');
    const [vehicleEndDate, setVehicleEndDate] = useState('');
    const [vehicleHistory, setVehicleHistory] = useState([]);
    const [vehicleError, setVehicleError] = useState('');

    // Add this function with other logic (e.g., after handleReportSearch)
   const handleVehicleHistorySearch = async () => {
    try {
        // Validate inputs
        if (!vehicleNumber || !vehicleStartDate || !vehicleEndDate) {
            setVehicleError('Please enter vehicle number and select date range.');
            return;
        }

        // Validate date format and check for invalid dates
        const startDateObj = new Date(vehicleStartDate);
        const endDateObj = new Date(vehicleEndDate);

        if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
            setVehicleError('Invalid date selected. Please choose valid dates.');
            return;
        }

        // Ensure start date is not after end date
        if (startDateObj > endDateObj) {
            setVehicleError('Start date cannot be after end date.');
            return;
        }

        const paymentsRef = collection(db, 'payments');
        const start = Timestamp.fromDate(startDateObj);
        const end = Timestamp.fromDate(endDateObj);

        const q = query(
            paymentsRef,
            where('vehicleNumber', '==', vehicleNumber.trim().toUpperCase()),
            where('dateTime', '>=', start),
            where('dateTime', '<=', end)
        );
        const querySnapshot = await getDocs(q);
        const historyData = [];

        querySnapshot.forEach((doc) => {
            const data = doc.data();
            historyData.push({
                zone:
                    data.zone === 'zone1'
                        ? 'Damber Chowk (Zone 1)'
                        : data.zone === 'zone2'
                        ? '9th Mile (Zone 2)'
                        : data.zone === 'zone3'
                        ? '10th Mile (Zone 3)'
                        : 'Unknown',
                startTime: data.dateTime
                    ? format(data.dateTime.toDate(), 'MMM dd, yyyy HH:mm')
                    : 'N/A',
                endTime: data.dateTimeEnd
                    ? format(data.dateTimeEnd.toDate(), 'MMM dd, yyyy HH:mm')
                    : 'N/A',
                amountPaid: data.amountPaid ? `₹${data.amountPaid}` : 'N/A',
            });
        });

        setVehicleHistory(historyData);
        setVehicleError(historyData.length === 0 ? 'No records found for the given vehicle number and date range.' : '');
    } catch (err) {
        console.error('Error fetching vehicle history:', err);
        setVehicleError('Error fetching vehicle history. Please check the inputs and try again.');
    }
};
    useEffect(() => {
        // Dialog is open by default when component mounts
    }, []);

    const handlePinSubmit = async () => {
        try {
            const docRef = doc(db, 'passkeys', 'adminkpg');
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const correctPin = docSnap.data().pin;
                if (pin === correctPin) {
                    setOpen(false);
                    setError('');
                } else {
                    setError('Incorrect PIN. Please try again.');
                    setTimeout(() => navigate(-1), 2000);
                }
            } else {
                setError('PIN not found in database.');
                setTimeout(() => navigate(-1), 2000);
            }
        } catch (err) {
            setError('Error fetching PIN. Please try again.');
            setTimeout(() => navigate(-1), 2000);
        }
    };

    const handleReportSearch = async () => {
        try {
            if (!startDate || !endDate || !reportType) {
                setError('Please select date range and report type.');
                return;
            }

            const paymentsRef = collection(db, 'payments');
            let q = query(paymentsRef);

            // Apply date range filter (highest priority)
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);

            q = query(
                q,
                where('dateTime', '>=', start),
                where('dateTime', '<=', end)
            );

            // Apply report type filters
            switch (reportType) {
                case 'zone1':
                    q = query(q, where('zone', '==', 'zone1'));
                    break;
                case 'zone2':
                    q = query(q, where('zone', '==', 'zone2'));
                    break;
                case 'zone3':
                    q = query(q, where('zone', '==', 'zone3'));
                    break;
                case 'allZones':
                    q = query(q, where('zone', 'in', ['zone1', 'zone2', 'zone3']));
                    break;
                case 'daily':
                    q = query(q, where('paymentType', '==', 'daily'));
                    break;
                case 'monthly':
                    q = query(q, where('paymentType', '==', 'monthly'));
                    break;
                case 'totalIncome':
                    // No additional filters for total income
                    break;
                default:
                    setError('Invalid report type selected.');
                    return;
            }

            const querySnapshot = await getDocs(q);
            let totalAmount = 0;
            const paymentCount = querySnapshot.size;

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                totalAmount += data.amountPaid || 0;
            });

            setReportData({
                totalPayments: paymentCount,
                totalAmount: totalAmount.toFixed(2),
                period: `${format(new Date(startDate), 'MMM dd, yyyy')} - ${format(new Date(endDate), 'MMM dd, yyyy')}`,
                reportType: reportType === 'zone1' ? 'Damber Chowk (Zone 1)' :
                    reportType === 'zone2' ? '9th Mile (Zone 2)' :
                        reportType === 'zone3' ? '10th Mile (Zone 3)' :
                            reportType === 'allZones' ? 'All Zones' :
                                reportType === 'daily' ? 'Daily Payments' :
                                    reportType === 'monthly' ? 'Monthly Payments' :
                                        'Total Income'
            });
            setError('');
        } catch (err) {
            console.error('Error fetching payments:', err);
            setError('Error fetching report data. Please check the inputs and try again.');
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={() => navigate(-1)}
                TransitionComponent={Fade}
                transitionDuration={300}
                PaperProps={{
                    sx: {
                        borderRadius: 1.5,
                        boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
                        backdropFilter: 'blur(8px)',
                        background: 'rgba(255,255,255,0.9)',
                        minWidth: 280,
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderRadius: '6px 6px 0 0',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    py: 1.2
                }}>
                    Admin Authentication
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 0.5 }}>
                    <Box sx={{ minHeight: '1.2rem' }} />
                    {error && (
                        <Fade in={!!error}>
                            <Alert
                                severity="error"
                                sx={{ mb: 1.2, borderRadius: 1, fontSize: '0.8rem' }}
                                variant="filled"
                            >
                                {error}
                            </Alert>
                        </Fade>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="PIN"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={pin}
                        onChange={(e) => setPin(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                                fontSize: '0.85rem',
                                height: 40,
                                '&:hover fieldset': {
                                    borderColor: 'primary.main',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '0.85rem'
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 1.2 }}>
                    <Button
                        onClick={() => navigate(-1)}
                        sx={{
                            borderRadius: 1,
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            px: 2,
                            '&:hover': {
                                bgcolor: 'action.hover',
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handlePinSubmit}
                        variant="contained"
                        sx={{
                            borderRadius: 1,
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            px: 2.5,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                            '&:hover': {
                                boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                            }
                        }}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            {!open && (
                <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', width: '100%' }}>
                    <Container maxWidth="md" sx={{ py: 2.5 }}>
                        <Fade in={!open}>
                            <div className="header" style={{ marginTop: '-12px', marginLeft: '30%' }}>
                                <img src="/logo.png" alt="Logo" className="logo" style={{ maxWidth: '70px' }} />
                                <div className="heading-text">
                                    <br />
                                    <h1 className="title" style={{ fontSize: '1.6rem' }}>Smart Parking App</h1>
                                    <h5 style={{ fontSize: '1.1rem', marginTop: '0px' }}>Administrative Console</h5>
                                </div>
                            </div>
                        </Fade>
                        <hr />
                        <br />
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2.5,
                                borderRadius: 1.5,
                                bgcolor: 'white',
                                boxShadow: '0 3px 15px rgba(0,0,0,0.05)'
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    mb: 2,
                                    fontWeight: 'medium',
                                    color: 'text.primary',
                                    fontSize: '1.1rem'
                                }}
                            >
                                Generate Parking Report
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                gap: 1.2,
                                mb: 2.5,
                                flexWrap: 'wrap',
                                alignItems: 'center'
                            }}>
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        minWidth: 150,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                            fontSize: '0.85rem',
                                            height: 40,
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />

                                <TextField
                                    label="End Date"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        minWidth: 150,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                            fontSize: '0.85rem',
                                            height: 40,
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />

                                <FormControl sx={{ minWidth: 150 }}>
                                    <InputLabel sx={{ fontSize: '0.85rem' }}>Report Type</InputLabel>
                                    <Select
                                        value={reportType}
                                        onChange={(e) => setReportType(e.target.value)}
                                        label="Report Type"
                                        sx={{
                                            borderRadius: 1,
                                            fontSize: '0.85rem',
                                            height: 40,
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        }}
                                    >
                                        <MenuItem value="zone1">Damber Chowk (Zone 1)</MenuItem>
                                        <MenuItem value="zone2">9th Mile (Zone 2)</MenuItem>
                                        <MenuItem value="zone3">10th Mile (Zone 3)</MenuItem>
                                        <MenuItem value="allZones">All Zones</MenuItem>
                                        <MenuItem value="daily">Daily Payments</MenuItem>
                                        <MenuItem value="monthly">Monthly Payments</MenuItem>
                                        <MenuItem value="totalIncome">Total Income</MenuItem>
                                    </Select>
                                </FormControl>

                                <Button
                                    variant="contained"
                                    onClick={handleReportSearch}
                                    sx={{
                                        height: 40,
                                        borderRadius: 1,
                                        textTransform: 'none',
                                        px: 2.5,
                                        fontWeight: 'medium',
                                        fontSize: '0.85rem',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                        '&:hover': {
                                            boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                                            bgcolor: 'primary.dark'
                                        }
                                    }}
                                >
                                    Generate Report
                                </Button>
                            </Box>

                            {error && (
                                <Fade in={!!error}>
                                    <Alert
                                        severity="error"
                                        sx={{ mb: 2, borderRadius: 1, fontSize: '0.8rem' }}
                                        variant="filled"
                                    >
                                        {error}
                                    </Alert>
                                </Fade>
                            )}

                            {reportData && (
                                <Fade in={!!reportData}>
                                    <TableContainer
                                        component={Paper}
                                        sx={{
                                            mt: 2,
                                            borderRadius: 1.5,
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{
                                                    bgcolor: 'primary.main',
                                                    '& th': {
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        py: 1.2,
                                                        fontSize: '0.85rem'
                                                    }
                                                }}>
                                                    <TableCell>Report Details</TableCell>
                                                    <TableCell>Value</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                                    <TableCell sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}>Report Type</TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{reportData.reportType}</TableCell>
                                                </TableRow>
                                                <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                                    <TableCell sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}>Period</TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{reportData.period}</TableCell>
                                                </TableRow>
                                                <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                                    <TableCell sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}>Total Parkings</TableCell>
                                                    <TableCell>
                                                        <Box sx={{
                                                            color: 'success.main',
                                                            fontWeight: 'bold',
                                                            fontSize: '0.95rem'
                                                        }}>
                                                            {reportData.totalPayments}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                                    <TableCell sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}>Total Amount</TableCell>
                                                    <TableCell>
                                                        <Box sx={{
                                                            color: 'info.main',
                                                            fontWeight: 'bold',
                                                            fontSize: '0.95rem'
                                                        }}>
                                                            ₹{reportData.totalAmount}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Fade>
                            )}
                        </Paper>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2.5,
                                mt: 2.5,
                                borderRadius: 1.5,
                                bgcolor: 'white',
                                boxShadow: '0 3px 15px rgba(0,0,0,0.05)'
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    mb: 2,
                                    fontWeight: 'medium',
                                    color: 'text.primary',
                                    fontSize: '1.1rem'
                                }}
                            >
                                Vehicle History
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                gap: 1.2,
                                mb: 2.5,
                                flexWrap: 'wrap',
                                alignItems: 'center'
                            }}>
                                <TextField
                                    label="Vehicle Number"
                                    value={vehicleNumber}
                                    onChange={(e) => setVehicleNumber(e.target.value)}
                                    placeholder="e.g., AB1234CD"
                                    sx={{
                                        minWidth: 150,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                            fontSize: '0.85rem',
                                            height: 40,
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={vehicleStartDate}
                                    onChange={(e) => setVehicleStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        minWidth: 150,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                            fontSize: '0.85rem',
                                            height: 40,
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />
                                <TextField
                                    label="End Date"
                                    type="date"
                                    value={vehicleEndDate}
                                    onChange={(e) => setVehicleEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        minWidth: 150,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                            fontSize: '0.85rem',
                                            height: 40,
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleVehicleHistorySearch}
                                    sx={{
                                        height: 40,
                                        borderRadius: 1,
                                        textTransform: 'none',
                                        px: 2.5,
                                        fontWeight: 'medium',
                                        fontSize: '0.85rem',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                        '&:hover': {
                                            boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                                            bgcolor: 'primary.dark'
                                        }
                                    }}
                                >
                                    Search History
                                </Button>
                            </Box>

                            {vehicleError && (
                                <Fade in={!!vehicleError}>
                                    <Alert
                                        severity="error"
                                        sx={{ mb: 2, borderRadius: 1, fontSize: '0.8rem' }}
                                        variant="filled"
                                    >
                                        {vehicleError}
                                    </Alert>
                                </Fade>
                            )}

                            {vehicleHistory.length > 0 && (
                                <Fade in={vehicleHistory.length > 0}>
                                    <TableContainer
                                        component={Paper}
                                        sx={{
                                            mt: 2,
                                            borderRadius: 1.5,
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{
                                                    bgcolor: 'primary.main',
                                                    '& th': {
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        py: 1.2,
                                                        fontSize: '0.85rem'
                                                    }
                                                }}>
                                                    <TableCell>Zone</TableCell>
                                                    <TableCell>Start Time</TableCell>
                                                    <TableCell>End Time</TableCell>
                                                    <TableCell>Amount Paid</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {vehicleHistory.map((record, index) => (
                                                    <TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>{record.zone}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>{record.startTime}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>{record.endTime}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>{record.amountPaid}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Fade>
                            )}
                        </Paper>
                    </Container>
                </Box>
            )}

        </>
    );
}

export default Admin;